import { type Range, useVirtualizer } from '@tanstack/react-virtual';
import type { Row } from '@tanstack/react-table';
import type { TableBodyProps } from 'common/Table';
import TableLoading from 'common/TableLoading';
import { memo, useCallback, useEffect, useMemo, useReducer, useRef } from 'react';
import { Colors } from 'styles';
import type { RightDrawerTableCellProps, RightDrawerTableItemType } from './types';

export const RIGHT_DRAWER_ROW_HEIGHT = 30;
export const RIGHT_DRAWER_ROW_OVERSCAN = 8;
export const RIGHT_DRAWER_MAX_ROW_OVERSCAN = 24;
export const RIGHT_DRAWER_SCROLL_IDLE_RESET_MS = 150;

const PAGINATION_STATUS_HEIGHT = 24;
const NOMINAL_FRAME_DURATION_MS = 16;
const FIXED_ROW_PROPS = {
  style: {
    boxSizing: 'border-box',
    height: '100%',
  },
} as const;
const LIGHTWEIGHT_CELL_PROPS = {
  isVirtualScrolling: true,
} as const satisfies RightDrawerTableCellProps;

type ScrollDirection = 'forward' | 'backward';

type AdaptiveOverscanState = {
  direction: ScrollDirection | null;
  lastOffset: number;
  lastTimestamp: number | null;
  overscan: number;
};

const getOverscanForSpeed = (speed: number): number => {
  if (speed >= 6) return RIGHT_DRAWER_MAX_ROW_OVERSCAN;
  if (speed >= 3) return 18;
  if (speed >= 1) return 12;
  return RIGHT_DRAWER_ROW_OVERSCAN;
};

const getAdaptiveRange = (
  { count, endIndex, overscan, startIndex }: Range,
  direction: ScrollDirection | null,
  adaptiveOverscan: number
): Array<number> => {
  const overscanBefore = direction === 'backward' ? adaptiveOverscan : overscan;
  const overscanAfter = direction === 'forward' ? adaptiveOverscan : overscan;
  const firstIndex = Math.max(startIndex - overscanBefore, 0);
  const lastIndex = Math.min(endIndex + overscanAfter, count - 1);

  return Array.from({ length: lastIndex - firstIndex + 1 }, (_, index) => firstIndex + index);
};

type RightDrawerVirtualRowProps = {
  cellProps?: RightDrawerTableCellProps;
  index: number;
  isLightweight: boolean;
  renderRow: TableBodyProps<RightDrawerTableItemType>['renderRow'];
  row: Row<RightDrawerTableItemType>;
  start: number;
};

type RightDrawerVirtualizedBodyProps = Omit<
  TableBodyProps<RightDrawerTableItemType>,
  'cellProps'
> & {
  cellProps?: RightDrawerTableCellProps;
};

const RightDrawerVirtualRow = memo(
  ({
    cellProps,
    index,
    isLightweight,
    renderRow,
    row,
    start,
  }: RightDrawerVirtualRowProps): React.ReactElement => (
    <div
      data-index={index}
      data-testid={`right-drawer-virtual-row-${index}`}
      data-virtual-row-mode={isLightweight ? 'lightweight' : 'interactive'}
      style={{
        height: RIGHT_DRAWER_ROW_HEIGHT,
        left: 0,
        position: 'absolute',
        top: 0,
        transform: `translateY(${start}px)`,
        width: '100%',
      }}
    >
      {renderRow({ row, cellProps, bodyRowProps: FIXED_ROW_PROPS })}
    </div>
  ),
  (previous, next) =>
    previous.isLightweight &&
    next.isLightweight &&
    previous.cellProps === next.cellProps &&
    previous.index === next.index &&
    previous.renderRow === next.renderRow &&
    previous.row === next.row &&
    previous.start === next.start
);

RightDrawerVirtualRow.displayName = 'RightDrawerVirtualRow';

export const RightDrawerVirtualizedBody = ({
  hasData,
  hasNextPage,
  loading,
  renderRow,
  rootRef,
  rows,
  sentryRef,
  cellProps,
  onScroll,
  dataTestId,
}: RightDrawerVirtualizedBodyProps): React.ReactElement => {
  const scrollElementRef = useRef<HTMLDivElement | null>(null);
  const idleResetTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const adaptiveOverscanRef = useRef<AdaptiveOverscanState>({
    direction: null,
    lastOffset: 0,
    lastTimestamp: null,
    overscan: RIGHT_DRAWER_ROW_OVERSCAN,
  });
  const [rangeRevision, refreshVirtualRange] = useReducer((revision) => revision + 1, 0);

  const setScrollElementRef = useCallback(
    (node: HTMLDivElement | null) => {
      scrollElementRef.current = node;
      if (node != null) adaptiveOverscanRef.current.lastOffset = node.scrollTop;
      rootRef(node);
    },
    [rootRef]
  );

  const getScrollElement = useCallback(() => scrollElementRef.current, []);
  const getItemKey = useCallback((index: number) => rows[index].id, [rows]);

  const rangeExtractor = useCallback(
    (range: Range) => {
      const { direction, overscan } = adaptiveOverscanRef.current;
      return getAdaptiveRange(range, direction, overscan);
    },
    // Changing the callback identity makes TanStack recalculate the range after the idle reset.
    // Scroll-time samples stay in refs so they do not trigger a React render for every event.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [rangeRevision]
  );

  const handleScrollCapture = useCallback((event: React.UIEvent<HTMLDivElement>) => {
    const currentOffset = event.currentTarget.scrollTop;
    const currentTimestamp = performance.now();
    const adaptiveOverscan = adaptiveOverscanRef.current;
    const offsetDelta = currentOffset - adaptiveOverscan.lastOffset;

    if (offsetDelta !== 0) {
      const elapsedSinceLastSample =
        adaptiveOverscan.lastTimestamp == null ||
        currentTimestamp - adaptiveOverscan.lastTimestamp > RIGHT_DRAWER_SCROLL_IDLE_RESET_MS
          ? NOMINAL_FRAME_DURATION_MS
          : Math.max(currentTimestamp - adaptiveOverscan.lastTimestamp, 1);
      const speed = Math.abs(offsetDelta) / elapsedSinceLastSample;
      const nextDirection = offsetDelta > 0 ? 'forward' : 'backward';
      const nextOverscan = Math.max(adaptiveOverscan.overscan, getOverscanForSpeed(speed));
      const shouldRefreshRange =
        nextDirection !== adaptiveOverscan.direction || nextOverscan !== adaptiveOverscan.overscan;

      adaptiveOverscan.direction = nextDirection;
      adaptiveOverscan.overscan = nextOverscan;
      adaptiveOverscan.lastOffset = currentOffset;
      adaptiveOverscan.lastTimestamp = currentTimestamp;

      // TanStack memoizes extracted indexes by visible range and callback identity. Refreshing only
      // on direction/tier transitions applies the new buffer without rerendering every scroll event.
      if (shouldRefreshRange) refreshVirtualRange();
    }

    if (idleResetTimerRef.current != null) clearTimeout(idleResetTimerRef.current);
    idleResetTimerRef.current = setTimeout(() => {
      const shouldRefreshRange =
        adaptiveOverscan.direction != null ||
        adaptiveOverscan.overscan !== RIGHT_DRAWER_ROW_OVERSCAN;

      adaptiveOverscan.direction = null;
      adaptiveOverscan.lastTimestamp = null;
      adaptiveOverscan.overscan = RIGHT_DRAWER_ROW_OVERSCAN;
      idleResetTimerRef.current = null;

      if (shouldRefreshRange) refreshVirtualRange();
    }, RIGHT_DRAWER_SCROLL_IDLE_RESET_MS);
  }, []);

  useEffect(
    () => () => {
      if (idleResetTimerRef.current != null) clearTimeout(idleResetTimerRef.current);
    },
    []
  );

  const virtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement,
    getItemKey,
    estimateSize: () => RIGHT_DRAWER_ROW_HEIGHT,
    overscan: RIGHT_DRAWER_ROW_OVERSCAN,
    rangeExtractor,
  });

  const totalSize = virtualizer.getTotalSize();
  const paginationStatusHeight = hasNextPage ? PAGINATION_STATUS_HEIGHT : 0;
  const lightweightCellProps = useMemo<RightDrawerTableCellProps>(
    () => (cellProps == null ? LIGHTWEIGHT_CELL_PROPS : { ...cellProps, isVirtualScrolling: true }),
    [cellProps]
  );
  const scrollOffset = virtualizer.scrollOffset ?? scrollElementRef.current?.scrollTop ?? 0;
  const viewportHeight =
    scrollElementRef.current?.clientHeight || virtualizer.scrollRect?.height || 0;
  const viewportEnd = scrollOffset + viewportHeight;
  const paginationStatusTop =
    loading && viewportHeight > 0
      ? Math.min(Math.max(scrollOffset + viewportHeight - PAGINATION_STATUS_HEIGHT, 0), totalSize)
      : totalSize;

  return (
    <div
      className="table-body"
      ref={setScrollElementRef}
      onScrollCapture={handleScrollCapture}
      onScroll={onScroll}
      css="flex: 1; overflow: auto; position: relative;"
      data-testid={dataTestId}
    >
      <TableLoading loading={loading} hasData={hasData} />
      <div
        data-testid="right-drawer-virtual-spacer"
        style={{
          height: totalSize + paginationStatusHeight,
          minWidth: '100%',
          position: 'relative',
        }}
      >
        {virtualizer.getVirtualItems().map((virtualRow) => {
          const row = rows[virtualRow.index];
          if (row == null) return null;

          const isVisible =
            viewportHeight === 0 ||
            (virtualRow.end > scrollOffset && virtualRow.start < viewportEnd);
          const isLightweight = virtualizer.isScrolling || !isVisible;

          return (
            <RightDrawerVirtualRow
              key={virtualRow.key}
              cellProps={isLightweight ? lightweightCellProps : cellProps}
              index={virtualRow.index}
              isLightweight={isLightweight}
              renderRow={renderRow}
              row={row}
              start={virtualRow.start}
            />
          );
        })}

        {hasNextPage && (
          <div
            data-testid="right-drawer-pagination-status"
            style={{
              alignItems: 'center',
              backgroundColor: loading ? Colors.gray1 : undefined,
              display: 'flex',
              height: PAGINATION_STATUS_HEIGHT,
              justifyContent: 'center',
              left: 0,
              position: 'absolute',
              top: paginationStatusTop,
              width: '100%',
              zIndex: loading ? 2 : undefined,
            }}
          >
            {loading ? 'Loading...' : <div ref={sentryRef} style={{ height: 1, width: '100%' }} />}
          </div>
        )}
      </div>
    </div>
  );
};

RightDrawerVirtualizedBody.displayName = 'RightDrawerVirtualizedBody';
